import {defineStore} from 'pinia'
import axios from "axios";
import type {Ref} from "vue";
import {ref, watch} from "vue";
import {useStatusHandler} from "@/composables/useStatusHandler";
import useWrapper from "@/composables/useWrapper";
import {useApiCredentials} from "@/composables/useApiCredentials";
import {IBaseStore} from "@/stores/BaseStore/interfaces/IBaseStore";

interface CrudStoreConfig<TItem> {
    storeId: string; // Store identifier
    route: string; // API route, e.g., "/{guid}/apps"
    initialize?: boolean; // Should the store reload on creation
    langSection?: string;
    transformItem?: (item: TItem) => TItem; // Optional transformation logic for items
    additionalActions?: Record<string, (...args: any[]) => any>;
}

export const useCrudStore = <TItem, TCreateRequest, TUpdateRequest, TStoreDefintion, IStore extends IBaseStore>(
    config: CrudStoreConfig<TItem>
) => {
    const {
        storeId,
        route,
        initialize = true,
        langSection = undefined,
        transformItem,
        additionalActions,
    } = config;

    const defintion = defineStore(storeId, <IStore>() => {
        const items: Ref<TItem[]> = ref([]);
        const initialized: Ref<boolean> = ref(false);
        const {rootGuid, rootAxiosBearerHeader, portalBaseUrl, appReady} = useApiCredentials();
        const {handleAxiosError, rootAuth} = useWrapper();
        const {handleStatus} = useStatusHandler();

        /** Resolve route template: replace {guid} and convert /portal/ routes to absolute URLs. */
        const resolveRoute = (template: string): string => {
            const resolved = template.replace('{guid}', rootGuid.value);
            return resolved.startsWith('/portal')
                ? portalBaseUrl + resolved.slice('/portal'.length)
                : resolved;
        };

        // Raw reload wrapped with rootAuth for token handling but
        // without handleAxiosError — used by the appReady watcher
        // so auth failures don't redirect to the login page.
        const _reloadRaw = rootAuth(async (): Promise<void> => {
            const {data} = await axios.get<TItem[]>(
                resolveRoute(route),
                rootAxiosBearerHeader.value
            );
            items.value = transformItem ? data.map(transformItem) : data;
        }, true);

        // Generalized Actions
        const reload = handleAxiosError(
            _reloadRaw,
            {errorMsg: `${langSection ? `${langSection}.reload.error` : 'stores.generic.reload.error'}`}
        );

        const create = handleAxiosError(
            rootAuth(async (payload: TCreateRequest): Promise<void> => {
                const {data, status} = await axios.post<TItem>(
                    resolveRoute(route),
                    payload, rootAxiosBearerHeader.value
                );
                items.value.push(data);
            }, true),
            {errorMsg: `${langSection ? `${langSection}.create.error` : 'stores.generic.create.error'}`,
                successMsg: `${langSection ? `${langSection}.create.success` : 'stores.generic.create.success'}`}
        );

        const update = handleAxiosError(
            rootAuth(async (id: number, payload: TUpdateRequest): Promise<void> => {
                const {status} = await axios.put(
                    `${resolveRoute(route)}/${id}`,
                    payload, rootAxiosBearerHeader.value
                );
            }, true),
            {errorMsg: `${langSection ? `${langSection}.update.error` : 'stores.generic.update.error'}`,
                successMsg: `${langSection ? `${langSection}.update.success` : 'stores.generic.update.success'}`}
        );

        const remove = handleAxiosError(
            rootAuth(async (id: number): Promise<void> => {
                const {status} = await axios.delete(
                    `${resolveRoute(route)}/${id}`,
                    rootAxiosBearerHeader.value
                );
                await reload();
            }, true),
            {errorMsg: `${langSection ? `${langSection}.remove.error` : 'stores.generic.remove.error'}`,
                   successMsg: `${langSection ? `${langSection}.remove.success` : 'stores.generic.remove.success'}`,
            }
        );

        // Add additional actions
        const customActions = additionalActions
            ? Object.fromEntries(
                Object.entries(additionalActions).map(([key, func]) => [
                    key,
                    func.bind({ rootGuid, rootAxiosBearerHeader, handleAxiosError, rootAuth }),
                ])
            )
            : {};

        // Initialize the store
        if (initialize) {
            reload()
                .then(() => {
                    initialized.value = true;
                })
                .catch(() => {
                    initialized.value = false;
                });
        }

        // Auto-reload when the application picker completes an app
        // switch (tokens acquired, permissions loaded).  Uses the raw
        // reload so auth failures (e.g. non-admin on the new app)
        // silently clear stale data instead of redirecting to login.
        watch(appReady, async () => {
            if (initialized.value) {
                try {
                    await _reloadRaw()
                } catch {
                    items.value = []
                }
            }
        })

        return {
            items,
            initialized,
            reload,
            create,
            update,
            remove,
            ...customActions,
        };
    });
    return defintion()
}


